import React, { useState } from "react";
import {
  Crown,
  CheckCircle2,
  AlertTriangle,
  Sparkles,
  ChevronDown,
  ChevronUp,
  Banknote,
  Scale,
  Wallet,
  Clock,
  Target,
  Flame,
  Calendar,
  ShieldCheck,
} from "lucide-react";
import { ProgressRing } from "../ProgressRing";

const avatarTone = {
  rose: "bg-rose-100 text-rose-700",
  blue: "bg-blue-100 text-blue-700",
  violet: "bg-indigo-100 text-indigo-700",
  amber: "bg-amber-100 text-amber-700",
  emerald: "bg-indigo-100 text-indigo-700",
};

const scoreColor = (s) => (s >= 85 ? "#6366F1" : s >= 70 ? "#F59E0B" : "#F43F5E");
const scoreText = (s) => (s >= 85 ? "text-indigo-700" : s >= 70 ? "text-amber-700" : "text-rose-700");

const Bar = ({ value, color }) => (
  <div className="flex items-center gap-2">
    <div className="flex-1 h-1.5 rounded-full bg-slate-100 overflow-hidden">
      <div className={`h-full rounded-full ${color}`} style={{ width: `${value}%` }} />
    </div>
    <span className="text-[10.5px] font-mono-cf text-slate-700 w-7 text-right">{value}%</span>
  </div>
);

const barColor = (v) => (v >= 80 ? "bg-indigo-500" : v >= 50 ? "bg-amber-500" : "bg-rose-500");

export const OfferCard = ({ offer, isRecommended }) => {
  const [open, setOpen] = useState(false);

  return (
    <article
      data-testid={`offer-card-${offer.id}`}
      className={`relative rounded-3xl border bg-white cf-shadow-lg transition-all duration-200 hover:-translate-y-1 hover:shadow-[0_24px_60px_rgba(15,23,42,0.10)] ${
        isRecommended ? "border-indigo-300/80 ring-2 ring-indigo-300/40" : "border-slate-200"
      }`}
    >
      {isRecommended && (
        <div className="absolute -top-3 left-6 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-gradient-to-r from-indigo-500 to-indigo-400 text-white text-[10px] font-bold uppercase tracking-[0.16em] shadow-[0_6px_20px_rgba(99, 102, 241,0.35)]">
          <Crown size={11} /> AI recommended
        </div>
      )}

      <div className="p-6">
        {/* Header row */}
        <div className="flex items-start gap-4">
          <div className={`w-12 h-12 rounded-xl flex items-center justify-center text-[14px] font-display font-bold ${avatarTone[offer.avatarColor]}`}>
            {offer.avatarInitials}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[10.5px] uppercase tracking-[0.16em] text-slate-500 font-semibold">Offer #{offer.rank}</p>
            <h3 className="font-display text-[18px] font-bold text-slate-900 leading-tight truncate">{offer.buyer}</h3>
            <span className={`inline-block mt-1 text-[10.5px] font-semibold uppercase tracking-[0.14em] px-2 py-0.5 rounded-full ${offer.typeChip}`}>{offer.type}</span>
          </div>
        </div>

        {/* Offer + Score */}
        <div className="mt-5 flex items-center gap-5">
          <ProgressRing
            value={offer.score}
            size={92}
            stroke={8}
            color={scoreColor(offer.score)}
            label={`${offer.score}`}
            sublabel="of 100"
          />
          <div className="flex-1 min-w-0">
            <p className="font-display text-[28px] font-bold text-slate-900 leading-none tracking-tight">{offer.offer}</p>
            <p className={`text-[12px] font-semibold mt-1 ${offer.deltaTone === "emerald" ? "text-indigo-700" : "text-rose-700"}`}>{offer.delta}</p>
            <p className={`text-[11.5px] uppercase tracking-[0.14em] font-bold mt-2 ${scoreText(offer.score)}`}>{offer.scoreLabel}</p>
            <p className="text-[11.5px] text-slate-500 mt-0.5 truncate">{offer.aiVerdict}</p>
          </div>
        </div>

        {/* Quick stats */}
        <div className="mt-5 grid grid-cols-3 gap-2">
          <Stat label="Speed" value={offer.speed} icon={Clock} />
          <Stat label="Fall-through" value={`${offer.fallthrough}%`} icon={AlertTriangle} tone={offer.fallthrough > 20 ? "rose" : offer.fallthrough > 10 ? "amber" : "emerald"} />
          <Stat label="Motivation" value={`${offer.motivation}`} icon={Flame} />
        </div>

        {/* Readiness bars */}
        <div className="mt-5 space-y-3">
          <Row icon={Scale} label="Solicitor" sub={offer.solicitor} value={offer.solicitorReady} />
          <Row icon={Wallet} label="Proof of funds" sub={offer.pof} value={offer.pofReady} />
          <Row icon={Banknote} label="Mortgage" sub={offer.mortgage} value={offer.mortgageState === "verified" ? 100 : offer.mortgageState === "offered" ? 92 : offer.mortgageState === "in_review" ? 60 : 30} />
        </div>

        {/* Chain */}
        <div className="mt-4 flex items-center gap-2.5 px-3.5 py-2.5 rounded-xl bg-slate-50 border border-slate-100">
          <ShieldCheck size={14} className={offer.chain.toLowerCase().includes("no chain") ? "text-indigo-600" : "text-amber-600"} />
          <div className="flex-1 min-w-0">
            <p className="text-[10px] uppercase tracking-[0.16em] text-slate-500 font-semibold">Chain</p>
            <p className="text-[12.5px] text-slate-900 font-medium truncate">{offer.chain}</p>
          </div>
          <span className="text-[10.5px] font-semibold uppercase tracking-[0.12em] text-slate-500">{offer.deposit}% deposit</span>
        </div>

        <button
          onClick={() => setOpen((o) => !o)}
          data-testid={`offer-card-${offer.id}-toggle`}
          className="mt-4 w-full inline-flex items-center justify-center gap-1 text-[12px] font-semibold text-slate-700 hover:text-indigo-700 transition-colors"
        >
          {open ? "Hide analysis" : "View deep analysis"}
          {open ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
        </button>

        {open && (
          <div className="mt-4 pt-4 border-t border-slate-100 space-y-3" data-testid={`offer-card-${offer.id}-detail`}>
            <div className="rounded-xl bg-slate-50 border border-slate-100 px-3.5 py-3">
              <p className="text-[10px] uppercase tracking-[0.16em] text-slate-500 font-semibold flex items-center gap-1.5"><Calendar size={11} /> Timeline</p>
              <p className="text-[12.5px] text-slate-900 mt-1">{offer.timeline}</p>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-[0.16em] text-slate-500 font-semibold mb-2">Signals</p>
              <ul className="space-y-1.5">
                {offer.flags.map((f, i) => (
                  <li key={i} className="flex items-start gap-2 text-[12.5px] text-slate-700">
                    <CheckCircle2 size={12} className="text-indigo-600 mt-0.5 shrink-0" />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
            </div>
            <p className="text-[12px] text-slate-600 italic">{offer.notes}</p>
            <div className="flex items-center gap-2 pt-1">
              <button className="h-9 px-3 rounded-lg bg-slate-900 hover:bg-slate-800 text-white text-[12px] font-semibold transition-colors">Accept offer</button>
              <button className="h-9 px-3 rounded-lg bg-white border border-slate-200 hover:border-slate-300 text-slate-700 text-[12px] font-semibold">Counter</button>
              <button className="h-9 px-3 rounded-lg bg-rose-50 border border-rose-200 hover:bg-rose-100 text-rose-700 text-[12px] font-semibold">Decline</button>
            </div>
          </div>
        )}
      </div>
    </article>
  );
};

const Stat = ({ label, value, icon: Icon, tone = "slate" }) => {
  const toneCls = {
    slate: "text-slate-900",
    emerald: "text-indigo-700",
    amber: "text-amber-700",
    rose: "text-rose-700",
  };
  return (
    <div className="rounded-xl border border-slate-100 bg-slate-50/60 px-3 py-2.5">
      <div className="flex items-center gap-1.5 text-slate-500">
        <Icon size={11} />
        <p className="text-[9.5px] uppercase tracking-[0.14em] font-semibold">{label}</p>
      </div>
      <p className={`font-display text-[15px] font-bold mt-1 ${toneCls[tone]}`}>{value}</p>
    </div>
  );
};

const Row = ({ icon: Icon, label, sub, value }) => (
  <div>
    <div className="flex items-center justify-between mb-1.5">
      <div className="flex items-center gap-1.5 min-w-0">
        <Icon size={12} className="text-slate-500 shrink-0" />
        <p className="text-[11.5px] font-medium text-slate-700 truncate">{label}</p>
      </div>
      <p className="text-[10.5px] text-slate-500 truncate ml-3 max-w-[60%] text-right">{sub}</p>
    </div>
    <Bar value={value} color={barColor(value)} />
  </div>
);
